import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from '../store';
import AjaxUtil from '../../axios/axios';

// Define the types based on the provided WhitelabelingConfig struct
export interface Feature {
    feature_name: string;
    permission: 'allowed' | 'forbidden' | string;
}

export interface Module {
    module_name: string;
    features: Feature[];
}

export interface FeatureFlags {
    modules: Module[];
}

export interface Logo {
    inapplogo: string;
    loginlogo: string;
}

export interface Font {
    font_type: string;
    font_highlight_color: string;
}

export interface IconColor {
    icon_color_primary: string;
    icon_color_secondary: string;
}

export interface Branding {
    logo: Logo;
    primary_color: string;
    font: Font;
    app_name: string;
    icon_color: IconColor;
}

export interface JsonConfig {
    branding: Branding;
    feature_flags: FeatureFlags;
}

export interface WhitelabelingConfig {
    id?: string;
    tenant_id: string;
    jsonconfig: JsonConfig;
    created_by?: string;
    updated_by?: string;
    created_at?: string;
    updated_at?: string;
}

export interface TenantState {
    config: WhitelabelingConfig | null;
    isLoading: boolean;
    error: string | null;
}

export const initialState: TenantState = {
    config: null,
    isLoading: true, // Start as true to prevent flickering during initial fetch
    error: null,
};

export const tenantSlice = createSlice({
    name: 'tenant',
    initialState,
    reducers: {
        setTenantLoading: (state, { payload }: PayloadAction<boolean>) => {
            state.isLoading = payload;
        },
        setTenantConfig: (state, { payload }: PayloadAction<WhitelabelingConfig>) => {
            state.config = payload;
            state.error = null;
        },
        setTenantError: (state, { payload }: PayloadAction<string>) => {
            state.error = payload;
        },
    },
});

export const { setTenantLoading, setTenantConfig, setTenantError } = tenantSlice.actions;

export const fetchTenantConfig = (hostname: string): AppThunk => async (dispatch) => {
    try {
        dispatch(setTenantLoading(true));
        const response: any = await AjaxUtil.sendRequest(`/whitelabeling/config/${hostname}`, null, false, { method: 'get', showError: false, isProfile: true });

        if (response && response.data) {
            dispatch(setTenantConfig(response.data));

            // Update document title if app_name is available
            if (response.data?.jsonconfig?.branding?.app_name) {
                document.title = response.data.jsonconfig.branding.app_name;
            }
        } else {
            // Fallback for mocked logic: using raw response directly if wrapper logic isn't strictly {"data": ...}
            dispatch(setTenantConfig(response));
            if (response?.jsonconfig?.branding?.app_name) {
                document.title = response.jsonconfig.branding.app_name;
            }
        }
    } catch (error: any) {
        console.error("Failed to fetch tenant configuration:", error);
        dispatch(setTenantError(error?.response?.data?.message || "Failed to fetch tenant configuration"));

        // Fallback title on error
        document.title = "Coguide";
    } finally {
        dispatch(setTenantLoading(false));
    }
};

export function tenantSelector(state: any) { return state.tenant; }
export function selectIsTenantLoading(state: any) { return state.tenant.isLoading; }

/**
 * Common helper to qualify a logo filename into a full URL.
 */
function qualifyLogo(logo: string | undefined): string | null {
    if (!logo) return null;
    if (logo.startsWith('http://') || logo.startsWith('https://') || logo.startsWith('/')) return logo;
    const tenantLogoBaseUrl = "https://go.coguide.in/c1/download/";
    return `${tenantLogoBaseUrl}${logo}`;
}

/**
 * Returns the fully-qualified URL for the login/signup page logo.
 */
export function selectLogoUrl(state: any): string | null {
    const logo: string | undefined = state.tenant.config?.jsonconfig?.branding?.logo?.loginlogo;
    const qualified = qualifyLogo(logo);

    // If not loading and no logo exists, show fallback
    if (!qualified && !state.tenant.isLoading) {
        return `${process.env.PUBLIC_URL}/images/coguide_main_logo.png`;
    }

    return qualified;
}

/**
 * Returns the fully-qualified URL for the in-app (sidebar) logo.
 */
export function selectInAppLogoUrl(state: any): string | null {
    const logo: string | undefined = state.tenant.config?.jsonconfig?.branding?.logo?.inapplogo;
    const qualified = qualifyLogo(logo);

    // If not loading and no logo exists, show fallback
    if (!qualified && !state.tenant.isLoading) {
        return `${process.env.PUBLIC_URL}/images/coguide_main_logo.png`;
    }

    return qualified;
}

/**
 * Returns the app name from tenant config, or a default fallback.
 */
export function selectAppName(state: any): string {
    return state.tenant.config?.jsonconfig?.branding?.app_name || 'Coguide';
}

/**
 * Returns a flattened dictionary of all tenant features mapped by lowercased name.
 * Ideal for O(1) checks directly equivalent to project permission arrays.
 */
export function selectTenantFeaturesMap(state: any): Record<string, boolean> {
    const modules: Module[] | undefined = state.tenant.config?.jsonconfig?.feature_flags?.modules;
    const featuresMap: Record<string, boolean> = {};

    if (!modules || modules.length === 0) return featuresMap;

    modules.forEach(m => {
        m.features?.forEach(f => {
            const key = f.feature_name?.toLowerCase()?.trim();
            if (key) {
                featuresMap[key] = f.permission === 'allowed';
            }
        });
    });

    return featuresMap;
}

/**
 * Checks whether a named feature is allowed by the tenant feature flags.
 * Searches across all modules in the config.
 * When no config is loaded yet, defaults to `false` (hide by default).
 * When the feature is not listed in any module, it is also `false` by default.
 * When duplicated across modules, the last occurrence wins.
 */
export function isFeatureAllowed(state: any, featureName: string): boolean {
    const map = selectTenantFeaturesMap(state);
    if (!featureName) return true;
    const result = map[featureName.toLowerCase().trim()];
    // Fail-open: if the feature isn't explicitly mentioned in the tenant config,
    // we allow it (and let project permissions decide).
    // If it is explicitly forbidden, it returns false.
    return result ?? true;
}

export default tenantSlice.reducer;
